import { createContext, useContext, useState, type ReactNode } from 'react';
import type { AuthUser, Role } from '../types';

const KC_URL       = 'https://auth.wastecloud.co';
const KC_REALM     = 'wastecloud-main';
const KC_CLIENT    = 'identity-admin-web';
const REDIRECT_URI = window.location.origin + '/';
const TK           = { access: 'wc_at', refresh: 'wc_rt' };

function randomB64(len: number): string {
  const a = new Uint8Array(len);
  crypto.getRandomValues(a);
  return btoa(String.fromCharCode(...a))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

async function sha256B64url(s: string): Promise<string> {
  const h = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
  return btoa(String.fromCharCode(...new Uint8Array(h)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

function b64urlDecode(s: string): string {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64.padEnd(b64.length + (4 - (b64.length % 4)) % 4, '=');
  return atob(padded);
}

function parseToken(token: string): AuthUser | null {
  try {
    const payload = JSON.parse(b64urlDecode(token.split('.')[1]));
    const clientRoles: string[] = payload?.resource_access?.[KC_CLIENT]?.roles ?? [];
    return {
      email: payload.email ?? payload.preferred_username ?? '',
      name:  payload.name,
      roles: clientRoles as Role[],
    };
  } catch {
    return null;
  }
}

function isExpired(token: string): boolean {
  try {
    const { exp } = JSON.parse(b64urlDecode(token.split('.')[1]));
    return Date.now() / 1000 > (exp - 30);
  } catch {
    return true;
  }
}

function saveTokens(data: { access_token: string; refresh_token?: string }) {
  sessionStorage.setItem(TK.access, data.access_token);
  if (data.refresh_token) sessionStorage.setItem(TK.refresh, data.refresh_token);
}

async function refreshTokens(): Promise<string | null> {
  const rt = sessionStorage.getItem(TK.refresh);
  if (!rt) return null;
  const resp = await fetch(
    `${KC_URL}/realms/${KC_REALM}/protocol/openid-connect/token`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token', client_id: KC_CLIENT, refresh_token: rt,
      }),
    }
  );
  if (!resp.ok) return null;
  const data = await resp.json();
  saveTokens(data);
  return data.access_token;
}

export interface AuthInitResult { user: AuthUser | null; token: string; }

export async function initAuth(): Promise<AuthInitResult> {
  const params   = new URLSearchParams(window.location.search);
  const code     = params.get('code');
  const verifier = sessionStorage.getItem('pkce_v');

  if (code && verifier) {
    window.history.replaceState({}, '', window.location.pathname);
    sessionStorage.removeItem('pkce_v');
    console.info('[auth] PKCE callback — exchanging code...');
    try {
      const resp = await fetch(
        `${KC_URL}/realms/${KC_REALM}/protocol/openid-connect/token`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            grant_type: 'authorization_code', client_id: KC_CLIENT,
            redirect_uri: REDIRECT_URI, code, code_verifier: verifier,
          }),
        }
      );
      if (resp.ok) {
        const data = await resp.json();
        saveTokens(data);
        const user = parseToken(data.access_token);
        console.info('[auth] Login OK, user:', user?.email);
        return { user, token: data.access_token };
      }
      console.error('[auth] Token exchange failed:', resp.status);
      return { user: null, token: '' };
    } catch (e) {
      console.error('[auth] Token exchange error:', e);
      return { user: null, token: '' };
    }
  }

  let at = sessionStorage.getItem(TK.access);
  if (at && isExpired(at)) {
    console.info('[auth] Token expired, refreshing...');
    at = await refreshTokens();
  }
  if (at) {
    const user = parseToken(at);
    console.info('[auth] Session restored, user:', user?.email);
    return { user, token: at };
  }

  console.info('[auth] No session.');
  return { user: null, token: '' };
}

export interface AuthContextValue {
  user:     AuthUser | null;
  token:    string;
  ready:    true;
  isViewer: boolean;
  canEdit:  boolean;
  isAdmin:  boolean;
  doLogin:  () => void;
  doLogout: () => void;
  getToken: () => Promise<string>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children, initialUser, initialToken }: {
  children: ReactNode; initialUser: AuthUser | null; initialToken: string;
}) {
  const [user,  setUser]  = useState<AuthUser | null>(initialUser);
  const [token, setToken] = useState<string>(initialToken);

  const roles     = user?.roles ?? [];
  const isViewer  = roles.includes('identity-viewer') || roles.includes('identity-operator') || roles.includes('identity-admin');
  const canEdit   = roles.includes('identity-operator') || roles.includes('identity-admin');
  const isAdmin   = roles.includes('identity-admin');

  const doLogin = async () => {
    const verifier  = randomB64(64);
    const challenge = await sha256B64url(verifier);
    sessionStorage.setItem('pkce_v', verifier);
    const params = new URLSearchParams({
      response_type:         'code',
      client_id:             KC_CLIENT,
      redirect_uri:          REDIRECT_URI,
      scope:                 'openid email profile',
      code_challenge:        challenge,
      code_challenge_method: 'S256',
    });
    window.location.href = `${KC_URL}/realms/${KC_REALM}/protocol/openid-connect/auth?${params}`;
  };

  const doLogout = () => {
    sessionStorage.removeItem(TK.access);
    sessionStorage.removeItem(TK.refresh);
    setUser(null);
    setToken('');
    const params = new URLSearchParams({ client_id: KC_CLIENT, post_logout_redirect_uri: REDIRECT_URI });
    window.location.href = `${KC_URL}/realms/${KC_REALM}/protocol/openid-connect/logout?${params}`;
  };

  const getToken = async (): Promise<string> => {
    let at = token || sessionStorage.getItem(TK.access) || '';
    if (!at || isExpired(at)) {
      const refreshed = await refreshTokens();
      if (refreshed) {
        setToken(refreshed);
        const u = parseToken(refreshed);
        if (u) setUser(u);
        return refreshed;
      }
      return '';
    }
    return at;
  };

  return (
    <AuthContext.Provider value={{ user, token, ready: true, isViewer, canEdit, isAdmin, doLogin, doLogout, getToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
